import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { FC } from 'react';

import { PythonLogs } from './PythonLogs';
import { ApplicationLog } from './ApplicationLog';
import { observer } from 'mobx-react-lite';

import './LogsOutput.css';
import { ApplicationState } from "../../machinelogic/constants";


type PossibleHTML = HTMLDivElement | undefined | null;
type EmptyObject = {};

interface ScrollDirection { down: boolean }
interface MergedLogs { hasNew: boolean, logs: ApplicationLog[] }


function mergeLogs(currentLogs: ApplicationLog[], newLogs: ApplicationLog[], limit: number): MergedLogs {
  // we assume that logs are in order

  newLogs = newLogs.filter(newLog => {
    const found = currentLogs.find(currentLog => currentLog.id == newLog.id);
    return !found;
  });

  const logs = [...currentLogs, ...newLogs];

  let truncated = false;
  // remove extras logs, and prepend the truncated log
  if (limit) {
    truncated = logs.length > (limit + 1); // +1 to take in account the possible  `... truncated` log
    if (truncated) {
      logs.splice(0, logs.length - limit, { message: '... truncated', id: logs[0].id - 1, timestamp: 0, level: 'truncated' });
    }
  }

  const hasNew = currentLogs.length != logs.length || truncated;
  return { hasNew, logs };
}


function isAbove(el: PossibleHTML, holder: PossibleHTML): boolean {
  if (!el || !holder) { return false; }
  const elRect = el.getBoundingClientRect();
  const holderRect = holder.getBoundingClientRect();
  return elRect.top <= holderRect.bottom;
}

interface LogsOutputProps {
  application: any
  limit: number
}

export const LogsOutput: FC<LogsOutputProps> = observer(({ application, limit = 0 }) => {
  // NOTE: we are using the reference instead of useState in order to facilitate the filtering/accumulation of logs
  // using simply `setLogs` would retrigger the render needlessly as the application state is polled quite frequently
  // in any cases, we still need a reference to the logs in the `onTrace` to extract new logs
  const logsRef = useRef<ApplicationLog[]>([]);
  const [hasNewLogTrigger, setHasNewLogTrigger] = useState<EmptyObject>({});

  const logsDivRef = useRef<HTMLDivElement>(null);

  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState<EmptyObject>({});
  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  // needed to calculate the scroll direction
  const lastScroll = useRef<number>(0);

  // we are using an object as an event, sowe dont need to reset it
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>({ down: false });
  const [currentAppState, setCurrentAppState] = useState<ApplicationState>(application.state);

  useEffect(() => {
    return () => { logsDivRef?.current?.removeEventListener("scroll", scrollCb); };
  }, []);


  // scrolling up, disable autoscrolling
  const scrollCb = useCallback(() => {
    const scrollTop = logsDivRef?.current?.scrollTop || 0;
    const isScrollingUp = scrollTop < lastScroll.current;
    lastScroll.current = scrollTop;

    if (isScrollingUp) {
      autoScroll && setAutoScroll(false);
      setScrollDirection({ down: false }); // technically not needed as it is coupled with autoscroll
    }
    else {
      setScrollDirection({ down: true });
    }
  }, []);


  // scroll the logs
  useEffect(() => {
    window.requestAnimationFrame(() => { logsDivRef?.current?.addEventListener("scroll", scrollCb); });

    if (autoScroll) {
      // disable the scrolling while we manually scroll to the last log
      logsDivRef?.current?.removeEventListener("scroll", scrollCb);
      logsDivRef.current?.querySelector('.python_logs')?.lastElementChild?.scrollIntoView();
    }

    return () => { logsDivRef?.current?.removeEventListener("scroll", scrollCb); };
  }, [scrollToBottomTrigger, hasNewLogTrigger, autoScroll, logsDivRef]);


  // reactivate auto-scrolling if the last log is in view
  useEffect(() => {
    if (!autoScroll && scrollDirection.down) {
      const lastLogUi = logsDivRef.current?.querySelector('.python_logs')?.lastElementChild as HTMLDivElement;
      if (isAbove(lastLogUi, logsDivRef?.current)) {
        setAutoScroll(true);
        setScrollToBottomTrigger({});
      }
    }
  }, [scrollDirection]);

  // susbscribe to machineLogic events
  useEffect(() => {
    if (currentAppState === ApplicationState.INACTIVE && application.state === ApplicationState.RUNNING) {
      logsRef.current = [];
    }

    setCurrentAppState(application.state);

    const listener = {
      onStateChanged: (state) => {
        logsDivRef?.current?.removeEventListener("scroll", scrollCb);
        if (state === 'running') {
          logsRef.current = [];
          setAutoScroll(true);
        }
      },
      onTrace: (data) => {
        // For now, we will assume that all logs are at index 0
        if (!data?.logging[0]?.logs) { return; }

        const { hasNew, logs: allLogs } = mergeLogs(logsRef.current, data.logging[0].logs, limit);
        logsRef.current = allLogs;

        if (hasNew) { setHasNewLogTrigger({}); }
      }
    };

    application.addListener(listener);

    return () => { application.removeListener(listener); };
  }, [application, application.state]);


  return (
    <div className='python_editor_output bordered'>
      <h3>Output</h3>
      <div className='python_editor_output_logs_container ml_scrollable' ref={logsDivRef}>
        <PythonLogs logs={logsRef.current} />
      </div>
    </div>
  );
});